import React, { useState } from 'react';
import { NavLink, useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { ROLE_LABEL } from '@/lib/labels';
import {
  LayoutDashboard, Building2, Users, UserSquare2, Package, Tags, Ticket, FileText,
  ShieldCheck, LogOut, Menu, X, ChevronDown, Search, Bell, User as UserIcon, PieChart, Activity, Mail, FileCode
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';

const MENU = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, roles: ['ADMIN','SUPERVISOR','CLIENT'] },
  { to: '/super-admin', label: 'Dashboard Global', icon: Activity, roles: ['SUPER_ADMIN'] },
  { to: '/reports', label: 'Relatórios', icon: FileText, roles: ['SUPER_ADMIN','ADMIN'] },
  { to: '/companies', label: 'Empresas', icon: Building2, roles: ['SUPER_ADMIN'] },
  { to: '/users', label: 'Usuários', icon: Users, roles: ['SUPER_ADMIN','ADMIN'] },
  { to: '/customers', label: 'Clientes', icon: UserSquare2, roles: ['SUPER_ADMIN','ADMIN','SUPERVISOR','ATTENDANT'] },
  { to: '/products', label: 'Produtos', icon: Package, roles: ['SUPER_ADMIN','ADMIN','SUPERVISOR'] },
  { to: '/categories', label: 'Categorias', icon: Tags, roles: ['SUPER_ADMIN','ADMIN','SUPERVISOR'] },
  { to: '/tickets', label: 'Tickets', icon: Ticket, roles: ['SUPER_ADMIN','ADMIN','SUPERVISOR','ATTENDANT','CLIENT'] },
  { to: '/email-logs', label: 'Histórico de Emails', icon: Mail, roles: ['SUPER_ADMIN','ADMIN'] },
  { to: '/email-templates', label: 'Templates de Email', icon: FileCode, roles: ['SUPER_ADMIN'] },
];

function initials(name) {
  if (!name) return '??';
  const parts = name.trim().split(' ');
  return ((parts[0]?.[0] || '') + (parts[parts.length - 1]?.[0] || '')).toUpperCase();
}

export default function AppLayout() {
  const { user, company, logout, hasRole } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const allowed = (item) => item.roles.some((r) => (user?.roles || []).some(role => role.type === r));
  const items = MENU.filter(allowed);

  const roleType = (user?.roles || [])[0]?.type;

  return (
    <div className="min-h-screen bg-slate-100 flex">
      {/* Sidebar */}
      <aside
        data-testid="sidebar"
        className={`fixed lg:static inset-y-0 left-0 z-40 w-64 text-slate-200 transform transition-transform lg:translate-x-0 ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}
        style={{ backgroundColor: theme.sidebarBgColor, color: theme.sidebarTextColor }}
      >
        <div className="h-16 px-5 flex items-center gap-2 border-b border-slate-700/60">
          {theme.logoUrl ? (
            <div className="w-9 h-9 rounded-lg overflow-hidden bg-white flex items-center justify-center">
              <img 
                src={`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}${theme.logoUrl}`} 
                alt={theme.companyName}
                className="w-full h-full object-contain"
              />
            </div>
          ) : (
            <div className="w-9 h-9 rounded-lg flex items-center justify-center shadow-md" style={{ backgroundColor: theme.primaryColor }}>
              <ShieldCheck className="w-5 h-5 text-white" />
            </div>
          )}
          <div>
            <div className="text-white font-semibold leading-tight">{theme.companyName}</div>
            <div className="text-[11px] text-slate-400 leading-tight">Gestão de garantias</div>
          </div>
          <button className="ml-auto lg:hidden text-slate-300" onClick={() => setMobileOpen(false)}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="py-4 px-3 space-y-1">
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              data-testid={`nav-${item.label.toLowerCase()}`}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'text-slate-300 hover:text-white hover:bg-slate-700/60'
                }`
              }
            >
              <item.icon className="w-4 h-4" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 border-t border-slate-700/60 p-4">
          <div className="flex items-center gap-3">
            <Avatar className="w-9 h-9 bg-blue-600">
              <AvatarFallback className="bg-blue-600 text-white text-xs">{initials(user?.name)}</AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-white truncate">{user?.name}</div>
              <div className="text-[11px] text-slate-400 truncate">{ROLE_LABEL[roleType] || roleType}</div>
            </div>
            <button data-testid="logout-btn" onClick={logout} className="text-slate-400 hover:text-red-400" title="Sair">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setMobileOpen(false)} />
      )}

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 bg-white border-b border-slate-200 sticky top-0 z-20 flex items-center px-4 lg:px-6 gap-4">
          <button className="lg:hidden text-slate-700" onClick={() => setMobileOpen(true)}>
            <Menu className="w-5 h-5" />
          </button>
          <div className="text-sm text-slate-500 hidden sm:block">
            {company?.name || (hasRole('SUPER_ADMIN') ? 'Painel administrativo' : '')}
          </div>
          <div className="ml-auto flex items-center gap-3">
            <button className="relative w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-600" title="Notificações">
              <Bell className="w-4 h-4" />
              <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-red-500" />
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu" className="flex items-center gap-2 rounded-lg px-2 py-1 hover:bg-slate-100">
                  <Avatar className="w-8 h-8">
                    <AvatarFallback className="bg-blue-600 text-white text-xs">{initials(user?.name)}</AvatarFallback>
                  </Avatar>
                  <div className="text-left hidden md:block">
                    <div className="text-sm font-medium text-slate-900 leading-tight">{user?.name}</div>
                    <div className="text-[11px] text-slate-500 leading-tight">{user?.email}</div>
                  </div>
                  <ChevronDown className="w-4 h-4 text-slate-500" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>{user?.name}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/profile')}>
                  <UserIcon className="w-4 h-4 mr-2" /> Meu perfil
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} className="text-red-600">
                  <LogOut className="w-4 h-4 mr-2" /> Sair
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
